import React from 'react';
import styled from 'styled-components';
import {MarkRequired} from 'ts-essentials';
import {COLORS} from '../../../constants/colors';
import {Z_INDEXES} from '../../../constants/layers';
import {tabFocusedStyles} from '../TabFocused/tabFocusedStyles';

const DURATION = 150;

type Props = Omit<
  MarkRequired<React.InputHTMLAttributes<HTMLInputElement>, 'aria-label'>,
  'type' | 'title'
> & {className?: string};

export const Toggle = ({
  className,
  checked,
  ref,
  ...rest
}: Props & {ref?: React.Ref<HTMLInputElement>}) => {
  return (
    <Wrapper
      className={className}
      data-testid="toggleWrapper"
      data-checked={checked}
    >
      <Input
        data-testid="toggle"
        type="checkbox"
        checked={checked}
        ref={ref}
        {...rest}
      />
      <Track data-testid="toggleTrack" />
    </Wrapper>
  );
};

const Track = styled.div`
  background-color: ${COLORS.Grey3};
  border-radius: 0.4375rem;
  height: 100%;
  left: 0;
  position: absolute;
  top: 0;
  width: 100%;
`;

const Input = styled.input`
  position: absolute;
  height: 3rem;
  width: 3rem;
  left: 50%;
  top: 50%;
  transform: translate(-50%, -50%);
  opacity: 0;
  z-index: ${Z_INDEXES.TOGGLE_INPUT};
  cursor: pointer;
`;

const Wrapper = styled.div`
  box-sizing: border-box;
  height: 0.875rem;
  position: relative;
  width: 2rem;

  @media (pointer: fine) {
    &:before {
      content: '';
      background-color: rgba(0, 0, 0, 0.05);
      border-radius: 100%;
      box-sizing: border-box;
      content: '';
      height: 3rem;
      left: -0.875rem;
      opacity: 0;
      position: absolute;
      top: 50%;
      transform: translateY(-50%);
      // eslint-disable-next-line local-rules/css-layout-animation
      transition: left ${DURATION}ms ease, opacity ${DURATION}ms ease;
      visibility: visible;
      width: 3rem;
      z-index: ${Z_INDEXES.ONE};
    }
  }

  &:after {
    background-color: ${COLORS.White};
    border-radius: 100%;
    box-shadow: 0 2px 2px 0 rgba(0, 0, 0, 0.24), 0 0 2px 0 rgba(0, 0, 0, 0.12);
    box-sizing: border-box;
    content: '';
    height: 1.25rem;
    left: 0;
    position: absolute;
    top: 50%;
    transform: translateY(-50%);
    // eslint-disable-next-line local-rules/css-layout-animation
    transition: left ${DURATION}ms ease;
    visibility: visible;
    width: 1.25rem;
    z-index: ${Z_INDEXES.ONE};
  }

  &:hover {
    &:before {
      opacity: 1;
    }
  }

  &:has(${Input}:focus-visible) {
    ${tabFocusedStyles({
      offset: '4px',
    })}
  }

  &[data-checked='true'] {
    ${Track} {
      background-color: ${COLORS.Green};
    }

    @media (pointer: fine) {
      &:before {
        left: -0.125rem;
      }

      &:hover {
        &:before {
          background-color: rgba(113, 225, 191, 0.1);
        }
      }
    }

    &:after {
      left: 1.2rem;
    }
  }
`;
